import { type GenericMap } from "@client/types";
import { isDate, isObject } from "lodash";
import { joinDefined } from "./misc";
import { capitalizeFirstLetter } from "./string";

export const clean = (data: object, shouldRemove: (x: any) => boolean): object =>
  Object.keys(data).reduce((res, x) => {
    if (shouldRemove(data[x])) return res;

    res[x] = data[x];
    return res;
  }, {});

export interface IsShallowEqualOptions {
  omitFunctions?: boolean;
  deep: boolean | string[];
  except?: Array<string>;
}

export const isShallowEqual = (a: any, b: any, opts?: IsShallowEqualOptions): boolean => {
  const equal = (a: any, b: any, opts?: IsShallowEqualOptions): boolean => {
    if (currentStackSize > 500) throw new Error("BUG. Circular reference");
    currentStackSize++;

    if (a === b) return true;
    if (!a && !b) return false;
    if (!a || !b) return false;

    if (Array.isArray(a) || Array.isArray(b)) {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      return a.every((aItem, index) => equal(aItem, b[index], opts));
    }

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((ak) => {
      if (a[ak] === b[ak]) return true;

      if (opts) {
        if (opts.omitFunctions && typeof a[ak] === "function" && typeof b[ak] === "function") return true;

        if (opts.except && opts.except.indexOf(ak) >= 0) return true;

        if (
          a[ak] &&
          b[ak] &&
          isObject(a[ak]) &&
          !isDate(a[ak]) &&
          ((typeof opts.deep === "boolean" && opts.deep) || (Array.isArray(opts.deep) && opts.deep.includes(ak)))
        )
          return equal(a[ak], b[ak], opts);
      }

      return false;
    });
  };

  let currentStackSize = 0;
  return equal(a, b, opts);
};

export const traverseObject = <T extends object>(
  obj: T,
  enter: (obj: T, key: string, path: string) => void | boolean
) => {
  const traverse = (obj: T, path: string) => {
    Object.keys(obj).forEach((k) => {
      if (obj[k] && typeof obj[k] === "object") {
        let proceed = true;
        let p = enter(obj, k, path);
        if (typeof p === "boolean") proceed = p;
        if (obj[k] && proceed) {
          traverse(obj[k], joinDefined(".", path, k));
        }
      } else {
        enter(obj, k, path);
      }
    });
  };

  traverse(obj, "");
};

export const mapObject = <T, R>(obj: GenericMap<any>, mapFn: (value: T) => R) =>
  Object.keys(obj).reduce((res, k) => ({ ...res, [k]: mapFn(obj[k]) }), {});

/** Doesn't modify the object */
export const omit = (obj: GenericMap<any>, props: any[]): GenericMap<any> =>
  Object.keys(obj).reduce((res, key) => {
    if (!props.some((p) => p == key)) {
      res[key] = obj[key];
    }
    return res;
  }, {});

export const addPrefix = (name: string, prefix?: string): string =>
  prefix ? `${prefix}${capitalizeFirstLetter(name)}` : name;
export const addPrefixIfNotAdded = (name: string, prefix?: string): string =>
  prefix && !name.startsWith(prefix) ? `${prefix}${capitalizeFirstLetter(name)}` : name;

export const pick = (obj: GenericMap<any> | null, props: Array<string | RegExp>, prefix?: string): GenericMap<any> => {
  if (!obj) return {};

  const allKeys = Object.keys(obj);

  return props.reduce((res, p) => {
    let prop = p;
    let convertFn = (v: any) => v;

    if (Array.isArray(p)) {
      prop = p[0];
      convertFn = p[1];
    }

    if (prop instanceof RegExp) {
      allKeys.forEach((key) => (<RegExp>prop).test(key) && (res[key] = obj[addPrefix(key, prefix)]));
    } else {
      res[<string>prop] = convertFn(obj[addPrefix(prop, prefix)]);
    }

    return res;
  }, {});
};

export const deepClone = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));

export const getFullPath = (path: string | undefined, name: string): string => (path ? `${path}.${name}` : name);
export const getParentPath = (path: string | undefined): string | undefined => {
  if (!path) return;
  if (!path.includes(".")) return;

  const parts = path.split(".");
  return parts.slice(0, parts.length - 1).join(".");
};

export interface KeyValuePair<TKey, TValue> {
  key: TKey;
  value: TValue;
}

export const pairs = <T>(obj: GenericMap<T>): KeyValuePair<string, T>[] =>
  Object.keys(obj).map((k) => ({
    key: k,
    value: obj[k]
  }));
export const values = <T>(obj: GenericMap<T>): T[] => Object.keys(obj).map((k) => obj[k]);

export const mapValues = <T, R = T>(obj: GenericMap<T>, mapFn: (item: T) => R): GenericMap<R> =>
  Object.keys(obj).reduce((res, key) => ({ ...res, [key]: mapFn(obj[key]) }), {});

export const renameObjectKey = (obj: object, oldKey: string, newKey: string) =>
  Object.keys(obj).reduce((acc, val) => {
    if (val === oldKey) {
      acc[newKey] = obj[oldKey];
    } else {
      acc[val] = obj[val];
    }
    return acc;
  }, {});

export const deepMergeWithSpread = (...objects: GenericMap<any>[]): GenericMap<any> => {
  const merge = (obj1: GenericMap<any>, obj2: GenericMap<any>): GenericMap<any> => {
    const result = { ...objects[0] };

    for (let key in obj2) {
      if (obj2.hasOwnProperty(key)) {
        if (obj2[key] instanceof Object && obj1[key] instanceof Object) {
          result[key] = deepMergeWithSpread(obj1[key], obj2[key]);
        } else {
          result[key] = obj2[key];
        }
      }
    }

    return result;
  };

  if (objects.length === 1) return objects[0];

  let result = { ...objects[0] };
  for (let i = 1; i < objects.length; i++) {
    result = merge(result, objects[i]);
  }

  return result;
};

const setWithSpreadBase = (obj: GenericMap<any>, path: string, value: any, isRemove: boolean) => {
  const result = {
    ...obj
  };

  let target = result;
  const parts = path.split(".");
  for (let i = 0; i < parts.length; i++) {
    const prop = parts[i];
    if (i === parts.length - 1) {
      if (isRemove) {
        delete target[prop];
      } else {
        target[prop] = value;
      }
    } else {
      target[prop] = {
        ...target[prop]
      };
      target = target[prop];
    }
  }
  return result;
};

export const setWithSpread = (obj: GenericMap<any>, path: string, value: any) =>
  setWithSpreadBase(obj, path, value, false);

export const unsetWithSpread = (obj: GenericMap<any>, path: string) => setWithSpreadBase(obj, path, undefined, true);
